/**
 * 视频舱选项 chip 单点(2026-08-06 编排页重排)。
 *
 * ═══ 为什么要抽 ═══════════════════════════════════════════════════════════════
 * 重排前同一串 `rounded-full border px-2.5 py-1 text-xs transition-colors` 在四处
 * 各手写一遍(VideoBriefPanel 比例/时长、VideoFormulaPicker 透镜、VoiceSubtitlePanel
 * 的私有 Chip、海报态「讲故事/忠于海报」)。四份拷贝的净效果是:
 *   ① 改一处对不齐其余三处;
 *   ② **四处全都没有 focus 环**(只有 transition-colors),键盘用户看不见自己在哪。
 *
 * ═══ radio 还是 toggle,不是样式问题 ═══════════════════════════════════════════
 * 目的 8 选 1、比例 3 选 1、时长 3 选 1 —— 这些是**单选组**,语义是 radio:
 * 读屏应该播报「8 项中的第 1 项」,方向键应该能在组内移动。此前它们全是裸 button +
 * `aria-pressed`(toggle 语义),读屏播报的是「未按下的按钮」,组的存在完全不可知。
 * 所以 `radio` 不是给样式用的开关,它决定这颗 chip 对辅助技术是什么东西。
 * 分组的 roving tabIndex 由 `OptionChipGroup` 统一处理,单颗 chip 不自己管。
 */
import { forwardRef, useRef, type KeyboardEvent, type ReactNode } from "react";

import { cn } from "@/shared/lib/utils";

export interface OptionChipProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  /** true = 单选组成员(role=radio + aria-checked);false = 独立开关(aria-pressed)。 */
  radio?: boolean;
  /** 单选组里只有选中项进 tab 序(roving tabIndex),由 OptionChipGroup 传。 */
  tabIndex?: number;
  title?: string;
  className?: string;
  /** e2e 定位钩子。 */
  "data-testid"?: string;
}

export const OptionChip = forwardRef<HTMLButtonElement, OptionChipProps>(function OptionChip(
  { active, onClick, children, disabled, radio, tabIndex, title, className, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      role={radio ? "radio" : undefined}
      aria-checked={radio ? active : undefined}
      aria-pressed={radio ? undefined : active}
      tabIndex={tabIndex}
      title={title}
      disabled={disabled}
      onClick={onClick}
      data-testid={rest["data-testid"]}
      className={cn(
        "cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        active
          ? "border-primary bg-primary/12 font-medium text-primary"
          : "border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground",
        disabled && "pointer-events-none opacity-50",
        className
      )}
    >
      {children}
    </button>
  );
});

/**
 * 单选组容器 —— `role="radiogroup"` + 方向键在组内移动焦点。
 *
 * ★为什么组要接管方向键:radio 组的既定交互是「Tab 进组一次,方向键在组内选」。
 * 若每颗 chip 都自带 tabIndex=0,8 个目的就要按 8 次 Tab 才能走完这一行 ——
 * 这正是我们把目的从 popover 提出来想避免的那种摩擦,只是换成了键盘上的。
 * 选中项(没有选中项时的第一项)是组里唯一的 tab 落点,由调用方按 `active` 传 tabIndex。
 */
export function OptionChipGroup({
  label,
  children,
  className,
  ...rest
}: {
  label: string;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const dir =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (!dir || !ref.current) return;
    const items = Array.from(
      ref.current.querySelectorAll<HTMLButtonElement>('[role="radio"]:not([disabled])')
    );
    if (items.length === 0) return;
    const cur = items.findIndex((n) => n === document.activeElement);
    // 没聚焦在组内时从选中项起步;循环到头折回(radio 组的既定行为)。
    const from = cur >= 0 ? cur : items.findIndex((n) => n.getAttribute("aria-checked") === "true");
    const next =
      items[((((from < 0 ? 0 : from) + dir) % items.length) + items.length) % items.length];
    e.preventDefault();
    next.focus();
    next.click();
  };

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={label}
      // ★组本身不进 tab 序(-1),但要可编程聚焦:radiogroup 的既定模式是「Tab 进组一次,
      //   方向键在组内选」,tab 落点是选中的那颗 radio(roving tabIndex),不是容器。
      //   写 -1 而不是省略,是因为 a11y lint 要求 interactive role 必须 focusable ——
      //   省略会红,写 0 又会让每个组多吃一次 Tab(那正是这套模式要避免的)。
      tabIndex={-1}
      onKeyDown={onKeyDown}
      data-testid={rest["data-testid"]}
      className={cn("flex flex-wrap gap-1.5", className)}
    >
      {children}
    </div>
  );
}
